import BenchlingConfig from 'BenchlingConfig';

import {BoxPlotXAxisSectionEditor, BoxPlotYAxisSectionEditor} from '../../editors/AxisEditors';
import {
  CUSTOM_COMPONENT_KEY,
  EXPANDABLE_DETAILS_KEY,
  FIELD_DEFAULTS_GENERATOR_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  INFO_TEXT_UI_SCHEMA_KEY,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchemaV2NestedShapeValues,
  SHOW_DIVIDER_LINE_KEY,
  StepConfigEditorOverride,
} from '../../StepConfigEditorTypes';
import {isSeriesColumnNameNull} from '../StepConfigDisableFieldFunctions';
import {getDefaultSeriesColorConfig, onChangeDiscreteColorPaletteMiddleware} from '../StepConfigUISchemas';

/**
 * Box Plot configuration sections for the Configure tab
 */
export const BOX_PLOT_CONFIG_SECTIONS = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'nameSection',
    'boxPointsSection',
    'xAxisSection',
    'yAxisSection',
    'seriesAndColorPaletteSection',
  ],
  nameSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['chartName'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    chartName: {
      displayName: 'Title',
      configShapePath: ['chartName'],
    },
  },
  boxPointsSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['boxPoints'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    boxPoints: {
      displayName: 'Outlier & point display',
      configShapePath: ['chartConfig', 'parameters', 'showPoints'],
      fieldSettings: {
        displayNames: {
          ALL: 'All points',
          OUTLIERS: 'Outliers only',
          HIDE: 'Hide points',
        },
      },
    },
  },
  xAxisSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['xAxisSection'],
    xAxisSection: {
      [CUSTOM_COMPONENT_KEY]: BoxPlotXAxisSectionEditor,
    },
  },
  yAxisSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisSection'],
    yAxisSection: {
      [CUSTOM_COMPONENT_KEY]: BoxPlotYAxisSectionEditor,
    },
  },
  seriesAndColorPaletteSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: BenchlingConfig.ENABLE_ANALYSIS_TOOL_NEW_BOXPLOT_FEATURES
      ? ['seriesSection', 'colorSection']
      : ['seriesSection'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    [SECTION_NAME_UI_SCHEMA_KEY]: BenchlingConfig.ENABLE_ANALYSIS_TOOL_NEW_BOXPLOT_FEATURES
      ? 'Series & Color Palette'
      : 'Series',
    seriesSection: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['seriesColumnName'],
      seriesColumnName: {
        displayName: 'Series',
        configShapePath: ['chartConfig', 'parameters', 'seriesColumnName'],
        fieldSettings: {
          noColumnSelectedText: 'No series',
          allowSelectNull: true,
        },
      },
    },
    colorSection: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['colorConfigSection', 'seriesColorConfigsSection'],
      [EXPANDABLE_DETAILS_KEY]: {
        headerField: 'colorConfigSection',
        detailFields: ['seriesColorConfigsSection'],
      },
      [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeDiscreteColorPaletteMiddleware,
      colorConfigSection: {
        displayName: 'Color palette',
        configShapePath: ['chartConfig', 'parameters', 'discreteColorPalette'],
        fieldSettings: {
          displayNames: {
            BENCHLING: 'Benchling (30)',
            DARK24: 'Dark (24)',
            LIGHT24: 'Light (24)',
            BOLD: 'Bold (11)',
            PASTEL: 'Pastel (11)',
            SAFE: 'Safe (11)',
            // Not named "Prism" for display to avoid confusion with Prism the company
            PRISM: 'Crystal (11)',
          },
          svgImagePaths: {
            BENCHLING: '/static/img/insights/palettes/benchling.svg',
            DARK24: '/static/img/insights/palettes/dark24.svg',
            LIGHT24: '/static/img/insights/palettes/light24.svg',
            BOLD: '/static/img/insights/palettes/bold.svg',
            PASTEL: '/static/img/insights/palettes/pastel.svg',
            SAFE: '/static/img/insights/palettes/safe.svg',
            PRISM: '/static/img/insights/palettes/prism.svg',
          },
        },
      },
      seriesColorConfigsSection: {
        configShapePath: ['chartConfig', 'parameters', 'seriesColorConfigs'],
        editorOverride: StepConfigEditorOverride.MAPPING_ARRAY,
        fieldSettings: {
          mappingSourcePath: ['chartConfig', 'parameters', 'seriesColumnName'],
          mappingKey: 'series',
          showNoMappingEditor: true,
          noMappingDisplayName: 'Base color',
          noMappingSentinel: '__BENCHLING_NO_SERIES_DEFAULT__',
        },
        elementUISchema: {
          [FIELD_ORDER_UI_SCHEMA_KEY]: ['series', 'color'],
          [FIELD_DEFAULTS_GENERATOR_KEY]: getDefaultSeriesColorConfig,
          series: {
            configShapePath: ['series'],
            disableFn: () => true,
            editorOverride: StepConfigEditorOverride.STRING_LABEL,
          },
          color: {
            configShapePath: ['color'],
            editorOverride: StepConfigEditorOverride.COLOR_PICKER,
          },
        },
      },
    },
  },
} as const satisfies ShapeUISchemaV2NestedShapeValues;

/**
 * Box Plot style sections for the Style tab
 */
export const BOX_PLOT_STYLE_SECTIONS = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['legendSection', 'pointStyleSection', 'boxStyleSection'],
  legendSection: {
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Legend',
    [INFO_TEXT_UI_SCHEMA_KEY]: 'A series column must be selected to show a legend.',
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['showLegend', 'position', 'customLabel'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    showLegend: {
      configShapePath: ['chartConfig', 'parameters', 'showLegend'],
      editorOverride: StepConfigEditorOverride.CHECKBOX,
      fieldSettings: {
        checkboxLabel: 'Show legend',
      },
      disableFn: isSeriesColumnNameNull,
    },
    position: {
      displayName: 'Position',
      configShapePath: ['chartConfig', 'parameters', 'legendLocation'],
      fieldSettings: {
        displayNames: {
          TOP: 'Top',
          LEFT: 'Left',
          RIGHT: 'Right',
        },
      },
      disableFn: isSeriesColumnNameNull,
    },
    customLabel: {
      displayName: 'Custom label (optional)',
      configShapePath: ['chartConfig', 'parameters', 'legendLabel'],
      disableFn: isSeriesColumnNameNull,
    },
  },

  pointStyleSection: {
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Point styling',
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['pointStyle', 'opacity', 'outlineOnly'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    [EXPANDABLE_DETAILS_KEY]: {
      headerField: 'pointStyle',
      detailFields: ['opacity', 'outlineOnly'],
    },
    pointStyle: {
      displayName: 'Point style',
      configShapePath: ['chartConfig', 'parameters', 'markerShape'],
      fieldSettings: {
        displayNames: {
          CIRCLE: 'Circle',
          DIAMOND: 'Diamond',
          SQUARE: 'Square',
          CROSS: 'Cross',
          X: 'X',
        },
        iconNames: {
          CIRCLE: 'circle',
          DIAMOND: 'diamond',
          SQUARE: 'square',
          CROSS: 'plus',
          X: 'xmark',
        },
      },
      editorOverride: StepConfigEditorOverride.ICON_SELECTOR,
    },
    opacity: {
      displayName: 'Opacity',
      configShapePath: ['chartConfig', 'parameters', 'markerOpacity'],
    },
    outlineOnly: {
      displayName: 'Outline only',
      configShapePath: ['chartConfig', 'parameters', 'markerShapeOpen'],
      editorOverride: StepConfigEditorOverride.CHECKBOX,
      fieldSettings: {
        checkboxLabel: 'Outline only',
      },
    },
  },
  boxStyleSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['fillOpacity', 'boxLineStyleSection'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Box style',
    fillOpacity: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['fillOpacity'],
      fillOpacity: {
        displayName: 'Fill opacity',
        configShapePath: ['chartConfig', 'parameters', 'fillOpacity'],
      },
    },
    boxLineStyleSection: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['lineWidth', 'lineWidthOverride'],
      [EXPANDABLE_DETAILS_KEY]: {
        headerField: 'lineWidth',
        detailFields: ['lineWidthOverride'],
      },

      lineWidth: {
        displayName: 'Line width',
        configShapePath: ['chartConfig', 'parameters', 'lineWidth'],
        fieldSettings: {
          displayNames: {
            THIN: 'Thin',
            THICK: 'Thick',
            NONE: 'None',
          },
          iconNames: {
            THIN: 'minus',
            THICK: 'rectangle-wide',
            NONE: 'ban',
          },
        },
        editorOverride: StepConfigEditorOverride.ICON_SELECTOR,
      },
      lineWidthOverride: {
        displayName: 'Line weight',
        configShapePath: ['chartConfig', 'parameters', 'lineWidthOverride'],
      },
    },
  },
} as const satisfies ShapeUISchemaV2NestedShapeValues;
